import { useRef, useState } from "react";
import RotatingBadge from "./RotatingBadge";
import HeroShader from "./HeroShader";

/**
 * Hero container with a custom scalloped bottom edge.
 * The section flex-grows to fill the remaining viewport height,
 * so announcement bar + navbar + hero together = 100vh.
 */
export default function HeroContainer() {
  const [isHovering, setIsHovering] = useState(false);
  const [origin, setOrigin] = useState({ x: 0.5, y: 0.5 });
  const [btnHover, setBtnHover] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const rafRef = useRef<number | null>(null);
  const targetRef = useRef({ x: 0.5, y: 0.5 });

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = wrapRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    const y = Math.min(1, Math.max(0, (e.clientY - rect.top) / rect.height));
    targetRef.current = { x, y };
    if (rafRef.current == null) {
      const tick = () => {
        setOrigin((prev) => {
          const nx = prev.x + (targetRef.current.x - prev.x) * 0.12;
          const ny = prev.y + (targetRef.current.y - prev.y) * 0.12;
          const dx = Math.abs(nx - targetRef.current.x);
          const dy = Math.abs(ny - targetRef.current.y);
          if (dx < 0.001 && dy < 0.001) {
            rafRef.current = null;
            return { x: targetRef.current.x, y: targetRef.current.y };
          }
          rafRef.current = requestAnimationFrame(tick);
          return { x: nx, y: ny };
        });
      };
      rafRef.current = requestAnimationFrame(tick);
    }
  };

  const shapePath = `M 40,0
             L 1320,0
             A 40,40 0 0 1 1360,40
             L 1360,520
             A 40,40 0 0 1 1320,560
             L 820,560
             A 40,40 0 0 0 780,600
             L 780,620
             A 40,40 0 0 1 740,660
             L 620,660
             A 40,40 0 0 1 580,620
             L 580,600
             A 40,40 0 0 0 540,560
             L 40,560
             A 40,40 0 0 1 0,520
             L 0,40
             A 40,40 0 0 1 40,0 Z`;
  return (
    <section className="relative flex-1 min-h-0 mx-auto w-full max-w-[1360px] px-6 mt-4 pb-4">
      <div
        ref={wrapRef}
        className="relative w-full h-full group"
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => {
          setIsHovering(false);
          targetRef.current = { x: 0.5, y: 0.5 };
          if (rafRef.current == null) {
            const tick = () => {
              setOrigin((prev) => {
                const nx = prev.x + (0.5 - prev.x) * 0.08;
                const ny = prev.y + (0.5 - prev.y) * 0.08;
                if (Math.abs(nx - 0.5) < 0.002 && Math.abs(ny - 0.5) < 0.002) {
                  rafRef.current = null;
                  return { x: 0.5, y: 0.5 };
                }
                rafRef.current = requestAnimationFrame(tick);
                return { x: nx, y: ny };
              });
            };
            rafRef.current = requestAnimationFrame(tick);
          }
        }}
        onMouseMove={handleMouseMove}
      >
        {/* Black container shape + embedded neuro-noise shader */}
        <svg
          viewBox="0 0 1360 660"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
          className="absolute inset-0 w-full h-full"
          preserveAspectRatio="none"
        >
          <defs>
            <clipPath id="hero-shape-clip">
              <path d={shapePath} />
            </clipPath>
          </defs>
          {/* Solid black fallback */}
          <path d={shapePath} fill="#0D0D0D" />
          {/* Shader clipped to the shape */}
          <foreignObject
            x="0"
            y="0"
            width="1360"
            height="660"
            clipPath="url(#hero-shape-clip)"
          >
            <div
              xmlns="http://www.w3.org/1999/xhtml"
              className={isHovering ? "hero-shader-hue" : ""}
              style={{
                width: "100%",
                height: "100%",
                opacity: isHovering ? 1 : 0.75,
                // When not hovering we keep a plain filter so the shader
                // returns to its calm blue state. On hover, the animated
                // class above takes over and shifts the hue over time.
                filter: isHovering ? undefined : "saturate(1) contrast(1)",
                transition: "opacity 600ms ease, filter 600ms ease",
              }}
            >
              {(() => {
                // Distance of the cursor from the container center (0..~0.7)
                const dx = origin.x - 0.5;
                const dy = origin.y - 0.5;
                const dist = Math.min(1, Math.sqrt(dx * dx + dy * dy) * 2);
                // While hovering, the whole blue pattern warps:
                //  - shifts toward the cursor
                //  - stretches (scale)
                //  - tilts (rotation)
                //  - speeds up + brightens
                const hoverAmt = isHovering ? 1 : 0;
                return (
                  <HeroShader
                    originX={origin.x}
                    originY={origin.y}
                    offsetX={dx * 0.6 * hoverAmt}
                    offsetY={dy * 0.6 * hoverAmt}
                    scale={1 + dist * 0.9 * hoverAmt}
                    rotation={dx * 40 * hoverAmt}
                    speed={1 + 1.8 * hoverAmt}
                    contrast={0.3 + 0.35 * hoverAmt}
                    brightness={0.05 + 0.1 * hoverAmt}
                  />
                );
              })()}
            </div>
          </foreignObject>
        </svg>

        {/* Content overlay */}
        <div className="absolute inset-0 flex flex-col">
          {/* Center content stack — fills the main body area */}
          <div className="flex-1 flex flex-col items-center justify-center text-center px-6 pb-[15%]">
            <RotatingBadge />
            <p className="mt-6 text-[13px] tracking-[0.32em] text-white/70 font-medium">
              JOHN SMITH
            </p>
            <h1 className="mt-4 font-display text-white text-[clamp(56px,7vw,104px)] leading-[0.95] tracking-tight">
              Thinking for
              <br />
              Creativity
            </h1>
          </div>

          {/* SCROLL DOWN label */}
          <p
            className="absolute left-1/2 -translate-x-1/2 text-[11px] tracking-[0.32em] text-white/80 font-medium"
            style={{ top: "78%" }}
          >
            SCROLL DOWN
          </p>

          {/* Decorative text — LEFT pocket (below hero, beside scroll tab) */}
          <div
            className="absolute left-0 flex items-center gap-6 pl-2 pr-6 overflow-hidden"
            style={{ top: "88%", width: "42%", height: "12%" }}
          >
            {/* Big stroke text — marquee */}
            <div className="flex-1 overflow-hidden">
              <div className="marquee-track flex items-center gap-8 whitespace-nowrap font-display text-[54px] leading-none stroke-text uppercase">
                <span>Design</span>
                <span className="text-ink">•</span>
                <span>Develop</span>
                <span className="text-ink">•</span>
                <span>Deliver</span>
                <span className="text-ink">•</span>
                <span>Design</span>
                <span className="text-ink">•</span>
                <span>Develop</span>
                <span className="text-ink">•</span>
                <span>Deliver</span>
                <span className="text-ink">•</span>
              </div>
            </div>
          </div>

          {/* Decorative text — RIGHT pocket */}
          <div
            className="absolute right-0 flex items-center justify-end gap-6 pl-6 pr-2 overflow-hidden"
            style={{ top: "88%", width: "42%", height: "12%" }}
          >
            {/* Big stroke text — marquee (reversed direction feel via layout) */}
            <div className="flex-1 overflow-hidden">
              <div className="marquee-track flex items-center gap-8 whitespace-nowrap font-display text-[54px] leading-none stroke-text uppercase">
                <span>Portfolio</span>
                <span className="text-ink">•</span>
                <span>2026</span>
                <span className="text-ink">•</span>
                <span>Studio</span>
                <span className="text-ink">•</span>
                <span>Portfolio</span>
                <span className="text-ink">•</span>
                <span>2026</span>
                <span className="text-ink">•</span>
                <span>Studio</span>
                <span className="text-ink">•</span>
              </div>
            </div>
          </div>

          {/* Scroll pill button — sits inside the scooped tab */}
          <button
            aria-label="Scroll down"
            onMouseEnter={() => setBtnHover(true)}
            onMouseLeave={() => setBtnHover(false)}
            className={`absolute left-1/2 -translate-x-1/2 z-10 h-[90px] rounded-full bg-white transition-all duration-300 flex items-center justify-center cursor-pointer ${
              btnHover ? "w-[168px] shadow-[0_0_0_4px_rgba(255,255,255,0.15)]" : "w-[140px]"
            }`}
            style={{ top: "84%" }}
          >
            <i
              className={`ri-arrow-down-line text-ink transition-all duration-200 ${
                btnHover
                  ? "text-3xl animate-bounce-down-fast"
                  : "text-2xl animate-bounce-down"
              }`}
            />
          </button>
        </div>
      </div>
    </section>
  );
}